<template>
    <div>
        <div class="mb-4 inline-flex rounded-lg border border-theme-border p-1">
            <button
                type="button"
                class="rounded-md px-3 py-1.5 text-sm font-medium transition"
                :class="
                    mode === 'palette'
                        ? 'bg-theme-hover text-theme-primary'
                        : 'text-theme-muted'
                "
                @click="setMode('palette')"
            >
                Elegir paleta
            </button>
            <button
                type="button"
                class="rounded-md px-3 py-1.5 text-sm font-medium transition"
                :class="
                    mode === 'custom'
                        ? 'bg-theme-hover text-theme-primary'
                        : 'text-theme-muted'
                "
                @click="setMode('custom')"
            >
                Personalizar colores
            </button>
        </div>

        <template v-if="mode === 'palette'">
            <palette-grid
                :palettes="visiblePalettes"
                :selected-id="selectedPaletteId"
                :business-name="businessName"
                :logo-url="logoUrl"
                :display-mode="displayMode"
                @select="selectPalette"
            />

            <button
                v-if="hasMorePalettes"
                type="button"
                class="mt-4 text-sm font-medium text-theme-primary hover:underline"
                @click="openMorePalettes"
            >
                Ver más paletas
            </button>

            <AppDialog v-model="morePalettesOpen" size="lg">
                <template #header>
                    <h2 class="text-xl font-semibold text-theme-base">
                        Paletas
                    </h2>
                </template>
                <palette-grid
                    :palettes="allPalettes"
                    :selected-id="selectedPaletteId"
                    :business-name="businessName"
                    :logo-url="logoUrl"
                    :display-mode="displayMode"
                    @select="selectPaletteFromModal"
                />
            </AppDialog>
        </template>

        <div v-else class="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div v-for="color in COLOR_FIELDS" :key="color.key">
                <label class="text-sm font-semibold text-theme-base block">
                    {{ color.label }}
                </label>
                <p class="text-xs text-theme-muted mb-3">
                    {{ color.hint }}
                </p>
                <color-input
                    :value="value[color.key]"
                    :placeholder="color.placeholder"
                    @input="updateColor(color.key, $event)"
                />
            </div>
        </div>
    </div>
</template>

<script>
import {
    COLOR_PALETTES,
    findPaletteByColors
} from "@/common/fields/color-palettes";

import ColorInput from "@/components/editor/ColorInput.vue";
import PaletteGrid from "@/components/editor/PaletteGrid.vue";
import AppDialog from "@/components/utils/AppDialog.vue";

const VISIBLE_PALETTES_COUNT = 6;

// Nombres pensados para que el cliente entienda para que sirve cada color,
// no para describir el campo tecnico (`primary`/`emphasis` siguen siendo las
// keys que espera el backend en `stackColors`).
export const COLOR_FIELDS = [
    {
        key: "primary",
        label: "Color de títulos",
        hint: "Nombre del negocio y encabezados",
        placeholder: "#284D6F"
    },
    {
        key: "emphasis",
        label: "Color de énfasis",
        hint: "Botones y redes sociales",
        placeholder: "#FD951E"
    },
    {
        key: "background",
        label: "Color de fondo",
        hint: "Fondo del catálogo",
        placeholder: "#FFFFFF"
    },
    {
        key: "text",
        label: "Color de texto",
        hint: "Texto del cuerpo",
        placeholder: "#000000"
    }
];

export default {
    name: "BrandColorPicker",

    components: { AppDialog, ColorInput, PaletteGrid },

    props: {
        value: {
            type: Object,
            required: true
        },
        businessName: {
            type: String,
            default: "Tu negocio"
        },
        logoUrl: {
            type: String,
            default: ""
        },
        displayMode: {
            type: String,
            default: "catalog"
        },
        // Onboarding entra directo a elegir paleta; las pantallas donde el
        // tenant ya tiene colores guardados (design/editor) abren en el modo
        // que corresponda a esos colores (ver `initialMode`).
        defaultMode: {
            type: String,
            default: "palette"
        },
        // "inline": el boton expande la lista en la misma pantalla;
        // "modal": el boton abre un dialog con todas; "all": ya se muestran
        // todas (el picker vive dentro de un modal) y no hay boton.
        morePalettes: {
            type: String,
            default: "inline",
            validator: (value) => ["inline", "modal", "all"].includes(value)
        }
    },

    data() {
        return {
            COLOR_FIELDS,
            mode: this.resolveMode(),
            showAllPalettes: false,
            morePalettesOpen: false,
            // Paginas como design.vue/editor.vue cargan los colores reales
            // de forma asincrona, despues de que este componente ya se creo
            // con un valor por defecto — el watcher de `value` recalcula el
            // modo cuando eso llega, pero solo mientras el usuario no haya
            // tocado el toggle a mano (si ya lo tocó, se respeta su eleccion).
            userSetMode: false
        };
    },

    computed: {
        selectedPaletteId() {
            return findPaletteByColors(this.value)?.id ?? "";
        },
        allPalettes() {
            return COLOR_PALETTES;
        },
        visiblePalettes() {
            if (this.morePalettes === "all" || this.showAllPalettes) {
                return COLOR_PALETTES;
            }
            const visible = COLOR_PALETTES.slice(0, VISIBLE_PALETTES_COUNT);
            const selected = COLOR_PALETTES.find(
                (palette) => palette.id === this.selectedPaletteId
            );
            if (selected && !visible.includes(selected)) {
                visible[visible.length - 1] = selected;
            }
            return visible;
        },
        hasMorePalettes() {
            return (
                this.morePalettes !== "all" &&
                !this.showAllPalettes &&
                COLOR_PALETTES.length > VISIBLE_PALETTES_COUNT
            );
        }
    },

    watch: {
        value: {
            deep: true,
            handler() {
                if (this.userSetMode) return;
                this.mode = this.resolveMode();
            }
        }
    },

    methods: {
        // Si los colores actuales calzan exacto con una paleta, se abre en
        // modo paleta con esa marcada; si no calzan con ninguna (el tenant ya
        // personalizo a mano), se abre directo en modo personalizar para que
        // vea sus colores reales, no una paleta que no es la suya.
        resolveMode() {
            if (findPaletteByColors(this.value)) return "palette";
            return this.defaultMode === "palette" ? "palette" : "custom";
        },

        setMode(mode) {
            this.userSetMode = true;
            this.mode = mode;
        },

        selectPalette(palette) {
            this.$emit("input", { ...palette.colors });
        },

        selectPaletteFromModal(palette) {
            this.selectPalette(palette);
            this.morePalettesOpen = false;
        },

        openMorePalettes() {
            if (this.morePalettes === "modal") {
                this.morePalettesOpen = true;
            } else {
                this.showAllPalettes = true;
            }
        },

        updateColor(key, colorValue) {
            this.$emit("input", { ...this.value, [key]: colorValue });
        }
    }
};
</script>
